// Login screen — matches the welcome overlay style: full-viewport, logo +
// Caveat wordmark, password input row, square accent arrow button.

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../api/client";

import "./Login.css";

export function Login() {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await apiFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        // Clear the welcome-dismissed flag so the overlay always shows after login.
        window.sessionStorage.removeItem("ophelia.welcome.dismissed");
        const next = params.get("next");
        navigate(next && next.startsWith("/") ? next : "/", { replace: true });
        return;
      }

      // 429 = brute-force lockout (server-side per-IP throttle, spec Step 0).
      setError(
        res.status === 429
          ? t("login.error_too_many_attempts")
          : t("login.error_wrong_password"),
      );
      setShaking(true);
      setPassword("");
      window.setTimeout(() => setShaking(false), 400);
    } catch {
      setError(t("login.error_network"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-shell">
      <div className="login-logo-row">
        <img src="/logo.svg" alt="" className="login-logo" />
        <span className="login-wordmark">ophelia</span>
      </div>
      <form
        className={`login-form${shaking ? " login-form--shake" : ""}`}
        onSubmit={onSubmit}
        autoComplete="off"
      >
        <label className="visually-hidden" htmlFor="login-password">{t("login.password_label")}</label>
        <div className="login-field">
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("login.password_placeholder")}
            autoFocus
            disabled={submitting}
            aria-invalid={error != null}
            aria-describedby={error ? "login-error" : undefined}
          />
          <button
            type="submit"
            className="login-submit"
            disabled={submitting || password.length === 0}
            aria-label="Sign in"
          >
            →
          </button>
        </div>
        {error && (
          <div id="login-error" className="login-error" role="alert">
            {error}
          </div>
        )}
      </form>
    </main>
  );
}
